import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Subject, interval, EMPTY } from 'rxjs';
import { switchMap, filter, map, distinctUntilChanged } from 'rxjs/operators';
import { environment } from '../../environments/environment';

export interface Job {
  id: number;
  job_type: string;
  entity_type: string | null;
  entity_id: number | null;
  status: 'queued' | 'running' | 'complete' | 'failed';
  message: string | null;
  created_at: string;
  completed_at: string | null;
}

const DISMISS_AFTER_MS = 8000;
const POLL_INTERVAL_MS = 3000;

@Injectable({ providedIn: 'root' })
export class JobService {
  private jobsSubject = new BehaviorSubject<Job[]>([]);
  jobs$ = this.jobsSubject.asObservable();

  private completionSubject = new Subject<Job>();
  completion$ = this.completionSubject.asObservable();

  private seenTerminal = new Set<number>();
  private dismissTimers = new Map<number, ReturnType<typeof setTimeout>>();
  private initialized = false;

  constructor(private http: HttpClient) {
    // Do an immediate fetch on startup to populate active jobs.
    this.fetch();

    // Poll only while there are queued/running jobs.
    this.jobsSubject.pipe(
      map(jobs => jobs.some(j => j.status === 'queued' || j.status === 'running')),
      distinctUntilChanged(),
      switchMap(hasActive => hasActive ? interval(POLL_INTERVAL_MS) : EMPTY),
    ).subscribe(() => this.fetch());
  }

  register(job: Job): void {
    const current = this.jobsSubject.value;
    if (!current.find(j => j.id === job.id)) {
      this.jobsSubject.next([job, ...current]);
    }
  }

  dismiss(jobId: number): void {
    this.removeJob(jobId);
  }

  getCompletions$(entityType: string, entityId: number | null) {
    return this.completion$.pipe(
      filter(job => {
        if (entityId === null) {
          return job.entity_type === entityType;
        }
        return job.entity_type === entityType && job.entity_id === entityId;
      })
    );
  }

  private fetch(): void {
    this.http.get<Job[]>(`${environment.apiUrl}/jobs/active`).subscribe({
      next: (jobs) => this.reconcile(jobs),
      error: () => {},
    });
  }

  private reconcile(fresh: Job[]): void {
    // On first poll, silently mark all already-terminal jobs as seen so we
    // don't toast for work that completed before the page loaded.
    if (!this.initialized) {
      this.initialized = true;
      for (const job of fresh) {
        if (job.status === 'complete' || job.status === 'failed') {
          this.seenTerminal.add(job.id);
        }
      }
      // Only surface actively queued/running jobs on first load
      const active = fresh.filter(j => j.status === 'queued' || j.status === 'running');
      this.jobsSubject.next(active);
      return;
    }

    const current = this.jobsSubject.value;
    const freshById = new Map(fresh.map(j => [j.id, j]));

    // Emit completions for newly-terminal jobs
    for (const job of fresh) {
      if ((job.status === 'complete' || job.status === 'failed') && !this.seenTerminal.has(job.id)) {
        this.seenTerminal.add(job.id);
        this.completionSubject.next(job);
        this.scheduleDismiss(job.id);
      }
    }

    // Merge: update existing entries; add new ones that aren't already terminal
    const merged: Job[] = [];
    const seen = new Set<number>();
    for (const job of current) {
      const updated = freshById.get(job.id);
      merged.push(updated ?? job);
      seen.add(job.id);
    }
    for (const job of fresh) {
      if (!seen.has(job.id) && !this.seenTerminal.has(job.id)) {
        merged.push(job);
      }
    }

    this.jobsSubject.next(merged);
  }

  private scheduleDismiss(jobId: number): void {
    if (this.dismissTimers.has(jobId)) return;
    const timer = setTimeout(() => {
      this.removeJob(jobId);
      this.dismissTimers.delete(jobId);
    }, DISMISS_AFTER_MS);
    this.dismissTimers.set(jobId, timer);
  }

  private removeJob(jobId: number): void {
    this.jobsSubject.next(this.jobsSubject.value.filter(j => j.id !== jobId));
  }
}
